import { Box, Container, Divider, Flex, HStack, Image, Link, SimpleGrid, Stack, Text } from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePublicLocale } from '../../../i18n/usePublicLocale';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../../../i18n/i18nCore';

const EMAIL = 'info@streamko.pro';

interface FooterLink {
  label: string;
  /** Same-document anchor target (built route-aware below). */
  href?: string;
  /** Internal SPA route (rendered via react-router Link). */
  to?: string;
}

/** Renders a footer link as an internal route, a mailto, or an anchor. */
function FooterLink({ link, ...rest }: { link: FooterLink } & Record<string, unknown>) {
  if (link.to) {
    return (
      <Link as={RouterLink} to={link.to} {...rest}>
        {link.label}
      </Link>
    );
  }
  return (
    <Link href={link.href} {...rest}>
      {link.label}
    </Link>
  );
}

/**
 * Temporary footer language switcher (EN / UK). Persists the choice to
 * localStorage via usePublicLocale. Replace with a proper header control once
 * the language UX is designed.
 */
function LanguageSwitcher() {
  const { locale, setLocale } = usePublicLocale();
  const codes = Object.keys(SUPPORTED_LANGUAGES) as SupportedLanguage[];
  return (
    <HStack as="nav" aria-label="Language" spacing="2px">
      <Box
        as="svg"
        viewBox="0 0 24 24"
        boxSize="16px"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        color="onboarding.mutedRose"
        mr="4px"
        aria-hidden
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18" />
        <path d="M12 3a15 15 0 0 1 0 18 15 15 0 0 1 0-18" />
      </Box>
      {codes.map((code) => (
        <Box
          as="button"
          type="button"
          key={code}
          onClick={() => {
            if (code !== locale) void setLocale(code);
          }}
          px="8px"
          py="3px"
          borderRadius="md"
          fontSize="sm"
          fontWeight={600}
          aria-pressed={locale === code}
          color={locale === code ? 'white' : 'onboarding.mutedRose'}
          bg={locale === code ? 'whiteAlpha.200' : 'transparent'}
          _hover={{ color: 'white' }}
          transition="color 0.15s"
        >
          {code.toUpperCase()}
        </Box>
      ))}
    </HStack>
  );
}

export function LandingFooter() {
  const { t } = useTranslation();
  const link = (k: string) => t(`landing.footer.links.${k}`);

  // The marketing sections only exist on the home route, so anchor links must
  // jump to home-with-hash when the footer is rendered on a standalone page.
  const onHome = useLocation().pathname === '/';
  const anchor = (id: string) => (onHome ? `#${id}` : `/#${id}`);

  const columns: { title: string; links: FooterLink[] }[] = [
    {
      title: t('landing.footer.columns.product'),
      links: [
        { label: link('how'), href: anchor('how-it-works') },
        { label: link('pricing'), href: anchor('pricing') },
        { label: link('faq'), href: anchor('faq') },
      ],
    },
    {
      title: t('landing.footer.columns.company'),
      links: [{ label: link('contact'), to: '/contacts' }],
    },
    {
      title: t('landing.footer.columns.legal'),
      links: [
        { label: link('terms'), to: '/terms' },
        { label: link('privacy'), to: '/privacy' },
      ],
    },
  ];

  return (
    <Box as="footer" bg="#140714" color="whiteAlpha.700">
      <Container maxW="1440px" px={{ base: 5, xl: '50px' }} py={{ base: 12, xl: '56px' }}>
        <Flex direction={{ base: 'column', lg: 'row' }} justify="space-between" gap={{ base: 10, lg: 16 }}>
          <Stack spacing="16px">
            <Box as="a" href={onHome ? '#hero' : '/'} aria-label="Streamko" display="inline-flex">
              <Image src="/images/landing/cmp-logo.png" alt="Streamko" h="30px" draggable={false} />
            </Box>
            <Link href={`mailto:${EMAIL}`} fontSize="md" color="white" _hover={{ color: 'white' }}>
              {t('landing.footer.emailLabel')} {EMAIL}
            </Link>
          </Stack>

          <SimpleGrid columns={{ base: 2, md: 3 }} spacingX={{ base: 8, md: '56px' }} spacingY="32px">
            {columns.map((col) => (
              <Stack key={col.title} spacing="12px" minW="110px">
                <Text fontSize="md" fontWeight={600} color="onboarding.mutedRose">
                  {col.title}
                </Text>
                {col.links.map((l) => (
                  <FooterLink key={l.label} link={l} fontSize="md" color="white" _hover={{ color: 'white' }} />
                ))}
              </Stack>
            ))}
          </SimpleGrid>
        </Flex>

        <Divider my={{ base: 8, xl: '32px' }} borderColor="whiteAlpha.200" />

        <Flex direction={{ base: 'column', sm: 'row' }} justify="space-between" align="center" gap={4}>
          <Text fontSize="sm" color="onboarding.mutedRose">
            {t('landing.footer.copyright')}
          </Text>
          <HStack spacing={{ base: 4, sm: '24px' }}>
            <LanguageSwitcher />
            <Link as={RouterLink} to="/terms" fontSize="sm" color="onboarding.mutedRose" _hover={{ color: 'white' }}>
              {link('terms')}
            </Link>
            <Link as={RouterLink} to="/privacy" fontSize="sm" color="onboarding.mutedRose" _hover={{ color: 'white' }}>
              {link('privacy')}
            </Link>
          </HStack>
        </Flex>
      </Container>
    </Box>
  );
}
